import { useState, useEffect, useMemo } from 'react';
import { uploadAllPhysicalFiles, signGet } from '../../lib/s3Upload';
import './TeacherEditQuestion.css';

function inferFromPath(urlOrKey) {
    if (!urlOrKey) return '';
    const path = urlOrKey.split('?')[0] || urlOrKey;
    const ext = path.split('.').pop()?.toLowerCase();
    if (!ext) return '';
    if (['jpg','jpeg','png','gif','webp'].includes(ext)) return 'image';
    if (['mp4','webm'].includes(ext)) return 'video';
    if (ext === 'pdf') return 'application/pdf';
    if (['doc','docx'].includes(ext)) return 'application/msword';
    return '';
}

function SignedMedia({ s3Key, legacyUrl, contentType, alt = 'media', size = 200 }) {
    const [url, setUrl] = useState(null);

    const kind = useMemo(() => {
        if (contentType) {
            if (contentType.startsWith('image/')) return 'image';
            if (contentType.startsWith('video/')) return 'video';
            if (contentType.startsWith('audio/')) return 'audio';
            if (contentType === 'application/pdf') return 'pdf';
            if (contentType.includes('msword') || contentType.includes('officedocument')) return 'doc';
        }
        const guessed = inferFromPath(s3Key || legacyUrl);
        if (guessed === 'image' || guessed === 'video' || guessed === 'audio') return guessed;
        if (guessed === 'application/pdf') return 'pdf';
        if (guessed === 'application/msword') return 'doc';
        return 'unknown';
    }, [contentType, s3Key, legacyUrl]);

    useEffect(() => {
        let mounted = true;
        async function run() {
            try {
                if (legacyUrl && /^http?:\/\//i.test(legacyUrl)) {
                    if (mounted) setUrl(legacyUrl);
                    return;
                }
                if (s3Key) {
                    const r = await signGet(s3Key);
                    if (mounted) setUrl(r.url);
                } else if (legacyUrl) {
                    const abs = legacyUrl.startsWith('http')
                        ? legacyUrl
                        : `${window.location.origin}${legacyUrl.startsWith('/') ? '' : '/'}${legacyUrl}`;
                    if (mounted) setUrl(abs);
                }
            } catch { /* ignore */ }
        }
        run();
        return () => { mounted = false; };
    }, [s3Key, legacyUrl]);

    if (!url) return null;

    if (kind === 'image') {
        return <img src={url} alt={alt} style={{ maxWidth: `${size}px`, maxHeight: `${size}px` }} />;
    }
    if (kind === 'video') {
        return <video controls style={{ maxWidth: `${Math.round(size*1.5)}px` }}>
            <source src={url} />
        </video>;
    }
    if (kind === 'audio') return <audio controls src={url} />;
    if (kind === 'pdf') return <a href={url} target="_blank" rel="noopener noreferrer">📄 Podgląd PDF</a>;
    if (kind === 'doc') return <a href={url} target="_blank" rel="noopener noreferrer">📄 Podgląd DOC</a>;
    return <a href={url} target="_blank" rel="noopener noreferrer">📎 Plik</a>;
}

function TeacherEditQuestion({ sectionId, questionId, initialData, onSave, onCancel }) {
    const [type, setType] = useState(initialData.type || 'single');
    const [content, setContent] = useState(initialData.content || '');
    const [points, setPoints] = useState(initialData.points || 1);
    const [negativePoints, setNegativePoints] = useState(initialData.negative_points || 0);
    const [noAnswerPoints, setNoAnswerPoints] = useState(initialData.no_answer_points || 0);
    const [tags, setTags] = useState(initialData.tags.join(',') || '');
    const [options, setOptions] = useState(initialData.options || []);
    const [optionFiles, setOptionFiles] = useState([]);
    const [questionFiles, setQuestionFiles] = useState([]);
    const [existingFiles, setExistingFiles] = useState(initialData.files || []);
    const [message, setMessage] = useState('');

    useEffect(() => {
        setType(initialData.type || 'single');
        setContent(initialData.content);
        setPoints(initialData.points);
        setNegativePoints(initialData.negative_points);
        setNoAnswerPoints(initialData.no_answer_points);
        setTags(initialData.tags.join(','));
        setOptions(initialData.options || []);
        setExistingFiles(initialData.files || []);
    }, [initialData]);

    const handleOptionChange = (index, value) => {
        const updated = [...options];
        updated[index].content = value;
        setOptions(updated);
    };

    const handleOptionFileChange = (index, file) => {
        const updated = [...optionFiles];
        updated[index] = file;
        setOptionFiles(updated);
    };

    const handleQuestionFilesChange = (e) => {
        setQuestionFiles(prev => [...prev, ...Array.from(e.target.files)]);
    };

    const removeQuestionFile = (index) => {
        setQuestionFiles(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingFile = async (fileId) => {
        try {
            const res = await fetch(`/api/prowadzacy/pytania/plik/${fileId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
            });
            if (!res.ok) throw new Error('Błąd usuwania pliku');
            setMessage('✅ Plik usunięty!');
            setExistingFiles(prev => prev.filter(f => f.file_id !== fileId));
        } catch (err) { setMessage('❌ ' + err.message); }
    };

    const removeOption = async (optionId) => {
        try {
            const res = await fetch(`/api/prowadzacy/odpowiedz/${optionId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
            });
            if (!res.ok) throw new Error('Błąd usuwania odpowiedzi');
            setOptions(prev => prev.filter(opt => opt.option_id !== optionId));
            setMessage('✅ Odpowiedź usunięta!');
        } catch (err) { setMessage('❌ ' + err.message); }
    };

    const renderFilePreview = (file) => {
        if (!file) return null;
        const t = file.type;
        if (t.startsWith('image/')) return <img src={URL.createObjectURL(file)} alt="Podgląd" style={{ maxHeight: '150px' }} />;
        if (t.startsWith('video/')) return <video controls src={URL.createObjectURL(file)} style={{ maxHeight: '150px' }} />;
        if (t.startsWith('audio/')) return <audio controls src={URL.createObjectURL(file)} />;
        return <p>{file.name}</p>;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        try {
            const physicalFiles = [
                ...questionFiles.map((f, idx) => ({ filename: f.name, contentType: f.type, size: f.size, scope: 'question', questionIndex: idx, file: f })),
                ...options.map((opt, i) => optionFiles[i] ? ({ filename: optionFiles[i].name, contentType: optionFiles[i].type, size: optionFiles[i].size, scope: 'option', optionIndex: i, file: optionFiles[i] }) : null).filter(Boolean)
            ];
            const { questionFilesMeta, optionMediaByIndex } = await uploadAllPhysicalFiles(physicalFiles);

            const payload = {
                question_id: questionId,
                type: type || 'single',
                content: content || '',
                points: Number(points) || 1,
                negative_points: Number(negativePoints) || 0,
                no_answer_points: Number(noAnswerPoints) || 0,
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
                questionFiles: questionFilesMeta,
                options: options.map((opt, i) => ({
                    option_id: opt.option_id || null,
                    content: opt.content,
                    is_correct: !!opt.is_correct,
                    media: optionMediaByIndex.get(i) || null
                }))
            };

            const res = await fetch(`/api/prowadzacy/pytania/${questionId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.blad || 'Błąd podczas edytowania pytania');

            setMessage('✅ Pytanie zaktualizowane!');
            onSave?.(data);
        } catch (err) {
            setMessage('❌ ' + (err.message || 'Błąd'));
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <h4>Edytuj pytanie</h4>
            {message && <p>{message}</p>}

            <label>Typ pytania:</label><br />
            <select value={type} onChange={e => setType(e.target.value)}>
                <option value="single">Jednokrotny wybór</option>
                <option value="multi">Wielokrotny wybór</option>
                <option value="text">Pole tekstowe</option>
                <option value="dropdown">Lista rozwijana</option>
                <option value="truefalse">Prawda/fałsz</option>
                <option value="numeric">Pole numeryczne</option>
                <option value="essay">Esej</option>
                <option value="textarea">Obszar tekstowy</option>
                <option value="fileupload">Załącznik</option>
                <option value="oral">Pytanie ustne</option>
            </select>

            <label>Treść pytania:</label><br />
            <textarea value={content} onChange={e => setContent(e.target.value)} required /><br />

            <label>Pliki pytania:</label><br />
            <div style={{ display: "flex", flexWrap: 'wrap', gap: '10px' }}>
                {existingFiles.map((file, i) => (
                    <div style={{ padding: '10px' }} key={`existing-${i}`}>
                        <SignedMedia
                            s3Key={file.s3_key}
                            legacyUrl={file.media_url}
                            contentType={file.content_type}
                            alt={`file-${file.file_id}`}
                        />
                        <button type="button" className="usun" onClick={() => removeExistingFile(file.file_id)}>❌</button>
                    </div>
                ))}
            </div>

            <label>Dodaj nowe pliki:</label><br />
            <input type="file" multiple onChange={handleQuestionFilesChange} />
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                {questionFiles.map((file, i) => (
                    <div key={`new-${i}`}>
                        {renderFilePreview(file)} <button type="button" onClick={() => removeQuestionFile(i)}>❌</button>
                    </div>
                ))}
            </div>

            {['single', 'multi', 'dropdown'].includes(type) && (
                <div>
                    <br /><br /><label>Odpowiedzi:</label><br />
                    {options.map((opt, i) => (
                        <div key={i} style={{ marginBottom: 12}}>
                        <div style={{display:'flex'}}>
                            {opt.option_id && <button type="button" className={'but-rozwin'} style={{ marginRight: '10px' }} onClick={() => removeOption(opt.option_id)}>❌</button>}
                            <input type="text" value={opt.content} onChange={e => handleOptionChange(i, e.target.value)} />
                            <input
                                type={type === 'single' ? 'radio' : 'checkbox'}
                                checked={!!opt.is_correct}
                                onChange={() => { const updated = [...options]; updated[i].is_correct = !opt.is_correct; setOptions(updated); }}
                            /> Poprawna
                        </div>

                            {(opt.media_s3_key || opt.media_url) && !optionFiles[i] && (
                                <div style={{ marginTop: 6 }}>
                                    <SignedMedia
                                        s3Key={opt.media_s3_key}
                                        legacyUrl={opt.media_url}
                                        contentType={opt.media_content_type}
                                        alt={`option-${opt.option_id}`}
                                        size={180}
                                    />
                                </div>
                            )}

                            <input type="file" onChange={e => handleOptionFileChange(i, e.target.files[0])} />
                            {optionFiles[i] && (<div style={{ marginTop: 6 }}>{renderFilePreview(optionFiles[i])}</div>)}
                        </div>
                    ))}
                    <button type="button" className={'but-rozwin'} style={{marginTop:10, marginBottom: 30}} onClick={() => setOptions([...options, { content: '', is_correct: false }])}>Dodaj opcję</button>
                </div>
            )}

            <label >Punkty:</label><br />
            <input type="number" value={points} onChange={e => setPoints(e.target.value)} /><br />
            <label>Za brak odpowiedzi:</label><br />
            <input type="number" value={noAnswerPoints} onChange={e => setNoAnswerPoints(e.target.value)} /><br />
            <label>Za błędną odpowiedź:</label><br />
            <input type="number" value={negativePoints} onChange={e => setNegativePoints(e.target.value)} /><br />

            <label>Tagi (przecinki):</label><br />
            <input type="text" value={tags} onChange={e => setTags(e.target.value)} /><br />

            <button className={'but-smal'} type="submit">Zapisz zmiany</button>
            {onCancel && <button type="button"  className={'but-smal'} style={{ marginLeft: 8 }} onClick={onCancel}>Anuluj</button>}
        </form>
    );
}

export default TeacherEditQuestion;
